// Arbres de lecture du portefeuille : culture › bioagresseur › projets, et
// filière › plan d'action › projets. Logique pure, sans dessin.
//
// Aucun lien direct n'existe dans la donnée entre une culture et un bioagresseur :
// ils sont reliés quand un même projet cite les deux. Un projet sans valeur à un
// niveau n'est pas masqué : il va dans une branche « non renseigné » (R6 du POC).

import { normaliser } from "./filtres";
import { comparerFr } from "./portefeuille";
import type { ClasseBioagresseur, Projet } from "./types";

export interface Branche {
  cle: string;
  libelle: string;
  /** Indices des projets de la branche, sans doublon. */
  projets: number[];
  /** Branches du niveau suivant ; vide au dernier niveau. */
  enfants: Branche[];
  /** Branche des projets sans valeur à ce niveau, rangée en fin. */
  nonRenseignee?: boolean;
}

/** Un niveau d'arbre : les valeurs (clé, libellé) d'un projet à ce niveau. */
export type Niveau = (p: Projet) => { cle: string; libelle: string }[];

/** Clé de la branche « non renseigné » ; un niveau peut aussi la rendre lui-même. */
export const NON_RENSEIGNE = "\u0000non-renseigne";

/** Construit l'arbre, les branches les plus fournies d'abord, « non renseigné » en fin. */
export function construireArbre(
  projets: Projet[],
  niveaux: Niveau[],
  libelleNonRenseigne: string[] = [],
  indices: number[] = projets.map((_, i) => i),
  profondeur = 0,
): Branche[] {
  const niveau = niveaux[profondeur];
  if (!niveau) return [];
  const groupes = new Map<string, { libelle: string; projets: number[] }>();
  for (const i of indices) {
    const p = projets[i];
    if (!p) continue;
    const valeurs = niveau(p);
    const liste = valeurs.length
      ? valeurs
      : [{ cle: NON_RENSEIGNE, libelle: libelleNonRenseigne[profondeur] ?? "non renseigné" }];
    const vues = new Set<string>();
    for (const v of liste) {
      if (vues.has(v.cle)) continue;
      vues.add(v.cle);
      const g = groupes.get(v.cle) ?? { libelle: v.libelle, projets: [] };
      g.projets.push(i);
      groupes.set(v.cle, g);
    }
  }
  return [...groupes.entries()]
    .map(([cle, g]) => ({
      cle,
      libelle: g.libelle,
      projets: g.projets,
      enfants: construireArbre(projets, niveaux, libelleNonRenseigne, g.projets, profondeur + 1),
      ...(cle === NON_RENSEIGNE ? { nonRenseignee: true } : {}),
    }))
    .sort(
      (a, b) =>
        Number(a.nonRenseignee ?? false) - Number(b.nonRenseignee ?? false) ||
        b.projets.length - a.projets.length ||
        comparerFr(a.libelle, b.libelle),
    );
}

/**
 * Recherche dans un arbre : une ligne reste si les termes se lisent sur elle ou
 * sur les lignes au-dessus. Une branche dont le libellé (avec ses parents) contient
 * tous les termes est gardée entière ; sinon on descend, et au dernier niveau un
 * projet reste si les termes se lisent sur sa propre ligne et le chemin qui y mène.
 *
 * Un projet trouvé par un autre de ses champs n'est donc pas montré sous toutes
 * ses branches : « vigne » ne fait pas apparaître la branche
 * Tomate parce qu'un projet sur tomate cite aussi la vigne.
 *
 * @param termes   déjà normalisés (minuscules, sans accents)
 * @param texteProjet texte normalisé de la ligne d'un projet
 */
export function elaguerParTexte(
  arbre: Branche[],
  termes: readonly string[],
  texteProjet: (indice: number) => string,
  chemin = "",
): Branche[] {
  const tout = (texte: string) => termes.every((t) => texte.includes(t));
  return arbre.flatMap((b) => {
    const ici = `${chemin} ${normaliser(b.libelle)}`;
    if (tout(ici)) return [b];
    if (b.enfants.length) {
      const enfants = elaguerParTexte(b.enfants, termes, texteProjet, ici);
      if (!enfants.length) return [];
      const gardes = new Set(enfants.flatMap((e) => e.projets));
      return [{ ...b, projets: b.projets.filter((i) => gardes.has(i)), enfants }];
    }
    const projets = b.projets.filter((i) => tout(`${ici} ${texteProjet(i)}`));
    return projets.length ? [{ ...b, projets }] : [];
  });
}

// --- cultures et bioagresseurs ------------------------------------------------

const capitaliser = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export const niveauCulture: Niveau = (p) =>
  p.cultures.map((c) => ({ cle: c.cle, libelle: capitaliser(c.nom.trim()) }));

/** Les classes visées (adventices, ravageurs, maladies), libellées par le référentiel. */
export const niveauBioagresseur =
  (classes: ClasseBioagresseur[]): Niveau =>
  (p) =>
    p.bioagresseurs.map((id) => ({
      cle: id,
      libelle: classes.find((c) => c.id === id)?.libelle ?? id,
    }));

// --- filières et plans d'action -------------------------------------------------

/**
 * Le préfixe d'un plan d'action désigne sa filière (« GC - Mildious » → Grandes
 * cultures). Table écrite d'après les plans présents dans la donnée ; un préfixe
 * inconnu rattache le plan à la filière du projet.
 */
const FILIERES_PAR_PREFIXE: [RegExp, string][] = [
  [/^f&l\s*frais\b/, "Fruits & légumes frais"],
  [/^f&l\s*trans\b/, "Fruits & légumes transformés"],
  [/^gc\b/, "Grandes cultures"],
  [/^om\b/, "Cultures ultramarines"],
  [/^vigne\b/, "Vigne"],
  [/^horti\b/, "Horticulture"],
  [/^ab\b/, "Mode de production agriculture biologique"],
  [/^ppam\b/, "PPAM & houblon"],
  [/^semences\b/, "Semences & plants"],
];

const PREFIXES = "(?:F&L|GC|OM|AB|PPAM|Horti|Semences|Vigne)\\b";

/** Plans d'action d'un projet : la cellule en porte souvent plusieurs. */
export function plansAction(cellule: string | null): string[] {
  if (!cellule) return [];
  return (
    cellule
      .split(";")
      // Une virgule ne sépare deux plans que si un préfixe de filière la suit :
      // « Pucerons, acariens et piqueurs suceurs » reste un seul plan.
      .flatMap((x) => x.split(new RegExp(`,\\s*(?=${PREFIXES})`)))
      .map((x) => x.replace(/\s+/g, " ").trim())
      .filter(Boolean)
  );
}

/** Clé de regroupement : casse, accents, guillemets et espaces autour du tiret ignorés. */
export const clePlan = (plan: string): string =>
  normaliser(plan)
    .replace(/[«»"“”]/g, "")
    .replace(/\s*-\s*/g, " - ")
    .replace(/\s+/g, " ")
    .trim();

export function filiereDuPlan(plan: string, projet: Pick<Projet, "filiere">): string | null {
  const cle = normaliser(plan);
  return FILIERES_PAR_PREFIXE.find(([motif]) => motif.test(cle))?.[1] ?? projet.filiere;
}

/** Libellé lisible : guillemets et espaces remis d'aplomb, rien d'autre. */
const libellePlan = (plan: string) =>
  plan
    .replace(/\s*-\s*/, " - ")
    .replace(/«\s*/g, "« ")
    .replace(/\s*»/g, " »")
    .replace(/\s+/g, " ")
    .trim();

/** Plans d'action d'un projet, avec la filière à laquelle chacun appartient. */
export function plansParFiliere(p: Projet): { filiere: string | null; plan: string }[] {
  return plansAction(p.planAction).map((plan) => ({
    filiere: filiereDuPlan(plan, p),
    plan: libellePlan(plan),
  }));
}

const SANS_FILIERE = "Plans sans filière";

export const niveauFilierePlan: Niveau = (p) =>
  plansParFiliere(p).map((x) =>
    x.filiere
      ? { cle: normaliser(x.filiere), libelle: x.filiere }
      : { cle: NON_RENSEIGNE, libelle: SANS_FILIERE },
  );

/**
 * Au niveau du plan, on ne garde que les plans de la filière parente : un projet
 * transversal présent sous trois filières n'y montre que les plans de chacune.
 */
export function construireArbreFilieres(projets: Projet[]): Branche[] {
  const filieres = construireArbre(projets, [niveauFilierePlan], [SANS_FILIERE]);
  return filieres.map((f) => ({
    ...f,
    enfants: construireArbre(
      projets,
      [
        (p) =>
          plansParFiliere(p)
            .filter((x) =>
              f.nonRenseignee ? !x.filiere : x.filiere !== null && normaliser(x.filiere) === f.cle,
            )
            .map((x) => ({ cle: clePlan(x.plan), libelle: x.plan })),
      ],
      ["plan non renseigné"],
      f.projets,
    ),
  }));
}

/** Intitulé d'un plan sans son préfixe de filière, quand la filière est déjà affichée au-dessus. */
export const intitulePlan = (plan: string): string =>
  plan.replace(new RegExp(`^${PREFIXES}(?:\\s*(?:frais|trans)\\b)?\\s*-\\s*`, "i"), "") || plan;
